import { useQuery } from "@tanstack/react-query";
import { ExternalLink, X } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../lib/api";
import { MarkdownPreview } from "./MarkdownPreview";

interface Props {
  noteId: string;
  onClose: () => void;
  onOpenFull: (id: string) => void;
}

/**
 * Read-only Note-Preview als Modal-Overlay.
 * Wird vom Graph aus aufgerufen, damit Zoom/Pan-State erhalten bleibt.
 * Wikilinks innen → laden eine andere Note ins selbe Modal (Stack-Like nicht nötig,
 * der User kann jederzeit "Im Editor öffnen" klicken um voll einzusteigen).
 */
export function NotePreviewDialog({ noteId, onClose, onOpenFull }: Props) {
  // Lokaler State erlaubt Wikilink-Navigation ohne Modal-Remount
  const [currentId, setCurrentId] = useState(noteId);

  useEffect(() => {
    setCurrentId(noteId);
  }, [noteId]);

  // Esc schließt
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const noteQ = useQuery({
    queryKey: ["note", currentId],
    queryFn: () => api.getNote(currentId),
    enabled: !!currentId,
  });

  async function handleWikilink(target: string) {
    // Wenn Wikilink auf existierende Note zeigt → im Modal nachladen.
    // Existiert nicht → ins Modal nicht erzeugen, das gehört in den Editor.
    const matches = await api.listNotes({ q: target, limit: 10 });
    const exact = matches.find((n) => n.title.toLowerCase() === target.toLowerCase());
    if (exact) {
      setCurrentId(exact.id);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex h-[85vh] w-[min(900px,92vw)] flex-col overflow-hidden rounded-lg border border-cyan-500/30 bg-zinc-950 shadow-[0_0_40px_-5px_rgba(34,211,238,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/60 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-zinc-100">
              {noteQ.data?.title ?? "Lädt…"}
            </div>
            {noteQ.data && (
              <div className="text-[10px] text-zinc-500">
                v{noteQ.data.version} ·{" "}
                {noteQ.data.tag_names.length > 0
                  ? noteQ.data.tag_names.map((t) => `#${t}`).join(" ")
                  : "keine Tags"}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onOpenFull(currentId)}
              className="flex items-center gap-1 rounded border border-cyan-500/30 px-2 py-1 text-xs text-cyan-200 hover:border-cyan-400 hover:text-cyan-100"
              title="In den Editor wechseln"
            >
              <ExternalLink className="h-3 w-3" />
              Im Editor öffnen
            </button>
            <button
              onClick={onClose}
              className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              title="Schließen (Esc)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-hidden">
          {noteQ.isLoading && (
            <div className="flex h-full items-center justify-center text-sm text-zinc-500">
              Lädt…
            </div>
          )}
          {noteQ.data && (
            <MarkdownPreview source={noteQ.data.content_md} onWikilinkClick={handleWikilink} />
          )}
        </div>
      </div>
    </div>
  );
}
